import React from "react"
import classNames from "classnames"
import Icon from "../Icon/Icon"
import {noop} from "lodash"
import ReactTooltip from "react-tooltip"
import {useEllipsisCheck} from "../../hooks/useEllipsisCheck"

export type ChipProps = {
	label: string
	onClick?: () => void
	isSelected?: boolean
	readonly?: boolean
	tooltip?: string
	tooltipKeyPrefix?: string
	maxWidth?: number
}

export type ChipsProps = {
	labels: string[]
	selectedChips?: string[]
	setSelected?: (label: string) => void
	readonly?: boolean
	tooltips?: (string | undefined)[]
	tooltipKeyPrefix?: string
	maxWidth?: number
}

function Chip({label, onClick, isSelected, readonly, tooltip, tooltipKeyPrefix, maxWidth}: ChipProps) {
	const {textRef, isTruncated} = useEllipsisCheck(label)

	const shouldShowTooltip = !!tooltip || isTruncated
	const tooltipText = tooltip ?? label
	const tooltipKeyString = (tooltipKeyPrefix ? tooltipKeyPrefix + "-" : "") + label
	const tooltipKey = shouldShowTooltip ? tooltipKeyString : undefined

	return (
		<div {...(shouldShowTooltip && {"data-tip": tooltipText, "data-for": tooltipKey, id: tooltipKey})}>
			{readonly ? (
				<div className="chip" style={{maxWidth}}>
					<div ref={textRef} className="chip-label">
						{label}
					</div>
				</div>
			) : (
				<div className={classNames("chip", "edit", isSelected && "selected")} onClick={onClick} style={{maxWidth}}>
					<div className="chip-label-and-icon-container">
						<span ref={textRef} className="chip-label">
							{label}
						</span>
						<div className={classNames("icon-button", "chip-action-icon", isSelected && "selected")}>
							<Icon icon="add-plus-small" size={16} />
						</div>
					</div>
				</div>
			)}
			{shouldShowTooltip && (
				<ReactTooltip
					id={tooltipKey}
					className={classNames("chip-tooltip", isTruncated && "ellipsis-tooltip")}
					place="top"
					effect="solid"
				/>
			)}
		</div>
	)
}

export function Chips({
	labels,
	selectedChips = [],
	setSelected = noop,
	readonly,
	tooltips = [],
	tooltipKeyPrefix,
	maxWidth,
}: ChipsProps) {
	return (
		<div className="chips-container">
			{labels.map((label, index) => (
				<Chip
					key={label}
					label={label}
					onClick={() => setSelected(label)}
					isSelected={selectedChips.includes(label)}
					readonly={readonly}
					maxWidth={maxWidth}
					{...(tooltips.length > 0 && {tooltip: tooltips[index]})}
					{...(tooltipKeyPrefix && {tooltipKeyPrefix})}
				/>
			))}
		</div>
	)
}

export default Chips
